import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../components/chalk/primitives";
import { inputStyle } from "../components/tournament/shared";
import { useAuth } from "../contexts/AuthContext";
import { director, tbilling, tournaments, usd } from "../lib/tournamentApi";
import { BalanceBanner } from "./TournamentUpgrade";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

/** Director annual plans: buy / manage + extras (handle, archive, signups, saved teams). */
export default function DirectorPlan() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => tbilling.director().then(setD).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  if (!d) return <div style={{ padding: 20 }}>Loading…</div>;
  const back = `${window.location.origin}/app/director`;
  return (
    <div data-testid="director-page" style={{ maxWidth: 680, margin: "0 auto", padding: "16px 16px 80px" }}>
      <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, margin: "4px 0 12px", lineHeight: 1 }}>Director billing</h1>
      {d.balance_due_cents > 0 && <BalanceBanner cents={d.balance_due_cents} back={back} />}
      {d.plan && d.plan.status !== "lapsed" ? <PlanStatus d={d} onChanged={load} /> : <BuyPlans d={d} back={back} />}
      {d.plan && <DirectorTools active={d.plan.active} />}
      {user?.is_master_test && <AdminGrant />}
    </div>
  );
}

function BuyPlans({ d, back }) {
  const [plan, setPlan] = useState("director");
  const [seats, setSeats] = useState(0);
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState(null);
  useEffect(() => { tbilling.terms().then(setTerms); }, []);
  if (!terms) return null;
  const total = d.plans[plan].price_cents + seats * terms.helper_seat_cents;
  async function buy() {
    try {
      const r = await tbilling.directorCheckout({ plan, helper_seats: seats, consent, consent_version: terms.consent_version, success_url: `${back}?plan=1`, cancel_url: back });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e.message); }
  }
  return (
    <div data-testid="director-buy">
      {d.plan?.status === "lapsed" && <div data-testid="director-lapsed" style={{ fontSize: 13, color: "var(--chalk-muted)", marginBottom: 10 }}>Your {d.plan.label} plan has ended. Past events and results stay public. Restart any time.</div>}
      {Object.entries(d.plans).map(([k, p]) => (
        <button key={k} data-testid={`director-plan-${k}`} onClick={() => setPlan(k)}
                style={{ display: "block", width: "100%", textAlign: "left", background: "#fff", border: `1.5px solid ${plan === k ? "var(--green-mid)" : "var(--chalk-border)"}`,
                         borderRadius: 12, padding: 14, marginBottom: 8, cursor: "pointer", font: "inherit", color: "inherit" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}><b>{p.label}</b><b>{usd(p.price_cents)}/yr</b></div>
          <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4 }}>
            {p.events ? `Up to ${p.events} Hub events a year, up to 32 teams each` : "Unlimited Hub events, up to 64 teams each"} · 5 helpers per event · saved teams, results archive, copy last tournament, custom link, next-tournament signups</div>
        </button>
      ))}
      <ChalkCard style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, fontSize: 13 }}>Extra helper seats <span style={{ color: "var(--chalk-muted)" }}>({usd(terms.helper_seat_cents)}/helper/yr)</span></div>
        <Stepper value={seats} onChange={setSeats} testId="director-seats" />
      </ChalkCard>
      <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 10 }}>
        Charged today: <b data-testid="director-total">{usd(total)}</b>. Renews yearly until you cancel. A {d.plans.director.label} event that finishes with 33–64 teams is charged the $80 band difference after the event.</div>
      <label data-testid="director-consent" style={{ display: "flex", gap: 10, fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="director-consent-checkbox" style={{ width: 18, height: 18, marginTop: 3 }} />
        <span>{terms.consent_text} <a href="/terms#tournament-billing" target="_blank" rel="noreferrer">Pricing terms</a></span>
      </label>
      <ChalkActionButton label={`Continue to checkout · ${usd(total)}`} testId="director-buy-btn" disabled={!consent || d.balance_due_cents > 0} onClick={buy} />
    </div>
  );
}

function PlanStatus({ d, onChanged }) {
  const p = d.plan;
  const [seats, setSeats] = useState(p.helper_seats || 0);
  async function change(body, ok) {
    try { await tbilling.directorChange(body); toast.success(ok); await onChanged(); } catch (e) { toast.error(e.message); }
  }
  const status = { active: "Active", canceling: `Ends ${fmtDate(p.period_end)}`, past_due: `Payment failed · access until ${fmtDate(p.grace_until)}` }[p.status];
  return (
    <ChalkCard data-testid="director-status" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 800, fontSize: 18 }} data-testid="director-plan-label">{p.label}</div>
        <div data-testid="director-plan-status" style={{ fontSize: 12, fontWeight: 700, color: p.status === "active" ? "var(--green-mid)" : "#B26B00" }}>{status}</div>
      </div>
      <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
        {p.status === "active" ? `Renews ${fmtDate(p.period_end)}` : `Plan year ends ${fmtDate(p.period_end)}`}{d.card_last4 ? ` · card ending ${d.card_last4}` : ""}</div>
      <div data-testid="director-usage" style={{ fontSize: 14, margin: "10px 0" }}>
        {p.limit ? <>Hub events: <b>{p.used}</b> live of {p.limit}{p.reserved ? ` · ${p.reserved} draft${p.reserved > 1 ? "s" : ""} reserved` : ""}</> : <>Hub events: <b>{p.used}</b> live · unlimited</>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, fontSize: 13 }}>Helpers per event: {5 + (p.helper_seats || 0)} (5 included + seats)</div>
        <Stepper value={seats} onChange={setSeats} testId="director-plan-seats" />
        {seats !== (p.helper_seats || 0) && <button data-testid="director-seats-save" onClick={() => change({ action: "helper_seats", helper_seats: seats }, "Helper seats updated.")} style={smallBtn}>Save</button>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {p.plan === "director" && <button data-testid="director-upgrade-plus" onClick={() => change({ action: "upgrade_plus" }, "Upgraded to Director Plus (prorated).")} style={smallBtn}>Upgrade to Director Plus</button>}
        {p.status === "canceling"
          ? <button data-testid="director-resume" onClick={() => change({ action: "resume" }, "Plan resumed.")} style={smallBtn}>Keep my plan</button>
          : <button data-testid="director-cancel" onClick={() => window.confirm("Cancel at the end of your plan year? Everything keeps working until then.") && change({ action: "cancel" }, "Plan will end at the end of your plan year.")} style={{ ...smallBtn, color: "#B22222" }}>Cancel plan</button>}
      </div>
    </ChalkCard>
  );
}

function DirectorTools({ active }) {
  const [handle, setHandle] = useState("");
  const [saved, setSaved] = useState(null);
  const [signups, setSignups] = useState(null);
  const [mine, setMine] = useState([]);
  const [target, setTarget] = useState("");
  useEffect(() => {
    director.handle().then((h) => setHandle(h.handle || ""));
    director.savedTeams().then((r) => setSaved(r.teams)).catch(() => setSaved([]));
    director.signups().then(setSignups).catch(() => {});
    tournaments.list().then((r) => { const l = r.tournaments || r; setMine(l.filter((x) => x.role !== "helper")); setTarget(l[0]?.id || ""); }).catch(() => {});
  }, []);
  async function run(fn, ok) { try { const r = await fn(); toast.success(ok(r)); } catch (e) { toast.error(e.message); } }
  return (
    <div data-testid="director-tools">
      <SectionTitle label="Results archive & link" />
      <ChalkCard style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--chalk-muted)" }}>{window.location.host}/d/</span>
          <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())} data-testid="director-handle-input" style={{ ...inputStyle, flex: 1 }} disabled={!active} />
          <button data-testid="director-handle-save" disabled={!active} onClick={() => run(() => director.setHandle(handle), () => "Link saved.")} style={smallBtn}>Save</button>
        </div>
        {handle && <Link to={`/d/${handle}`} data-testid="director-archive-link" style={{ fontSize: 12, fontWeight: 700, color: "var(--green-mid)" }}>Open public results archive →</Link>}
      </ChalkCard>
      <SectionTitle label={`Next-tournament signups (${signups?.count ?? 0})`} />
      <ChalkCard style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 8 }}>Fans and families sign up on your Hub and results pages (adults or parents/guardians only). Send one email when your next event is ready.</div>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="director-notify-select" style={{ ...inputStyle, flex: 1 }}>
            {mine.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button data-testid="director-notify" disabled={!active || !target || !signups?.count} onClick={() => run(() => director.notify(target), (r) => `Emailed ${r.sent} signup${r.sent === 1 ? "" : "s"}.`)} style={smallBtn}>Email signups</button>
        </div>
      </ChalkCard>
      <SectionTitle label={`Saved teams (${saved?.length ?? 0})`} />
      <ChalkCard data-testid="director-saved-teams">
        {(saved || []).slice(0, 50).map((s) => (
          <div key={s.name} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
            <span>{s.name}</span><span style={{ color: "var(--chalk-muted)" }}>{s.captain_email || ""} · {s.events} event{s.events > 1 ? "s" : ""}</span></div>
        ))}
        {!saved?.length && <div style={{ fontSize: 13, color: "var(--chalk-muted)" }}>Teams from your tournaments show up here. Import them from a tournament's Settings.</div>}
      </ChalkCard>
    </div>
  );
}

function AdminGrant() {
  const [email, setEmail] = useState("");
  return (
    <ChalkCard data-testid="admin-free-grant" style={{ marginTop: 18 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Admin: grant a free first Hub event (no card)</div>
      <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 8 }}>The director needs a ChalkOS account first. The grant is audited and used on their next upgrade.</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="director@email.com" data-testid="admin-grant-email" style={{ ...inputStyle, flex: 1 }} />
        <button data-testid="admin-grant-btn" style={smallBtn} onClick={async () => {
          try { await tbilling.adminGrant({ email }); toast.success("Granted."); setEmail(""); } catch (e) { toast.error(e.message); }
        }}>Grant</button>
      </div>
    </ChalkCard>
  );
}

function Stepper({ value, onChange, testId }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button aria-label="Fewer" data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(0, value - 1))} style={stepBtn}><Minus size={14} /></button>
      <span data-testid={`${testId}-value`} style={{ minWidth: 18, textAlign: "center", fontWeight: 700 }}>{value}</span>
      <button aria-label="More" data-testid={`${testId}-plus`} onClick={() => onChange(Math.min(50, value + 1))} style={stepBtn}><Plus size={14} /></button>
    </div>
  );
}

const smallBtn = { background: "transparent", border: "0.5px solid var(--chalk-border)", borderRadius: 8, padding: "7px 12px", color: "var(--green-mid)",
                   fontWeight: 700, fontSize: 12, cursor: "pointer" };
const stepBtn = { width: 30, height: 30, borderRadius: 8, border: "0.5px solid var(--chalk-border)", background: "#fff", cursor: "pointer", display: "grid", placeItems: "center" };
